import React, { useEffect, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { motion } from 'framer-motion';
import { TrendingUp, Building2, Plus, LogOut, Edit2, Settings, Check, X, Wifi, WifiOff, Users, DollarSign, Percent, Trash2, RefreshCw, BarChart3, Smartphone, Monitor, Tablet, MessageSquare } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogDescription } from '@/components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Switch } from '@/components/ui/switch';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { toast } from 'sonner';
import { tenantService, settlementService, analyticsService, api } from '@/lib/api';

const SuperAdminDashboard = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [tenants, setTenants] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [showEdit, setShowEdit] = useState(false);
  const [editingTenant, setEditingTenant] = useState(null);
  const [filterActive, setFilterActive] = useState('active'); // 'all', 'active', 'inactive' - default to active
  const [verifyingUltraMSG, setVerifyingUltraMSG] = useState(null);
  const [ultraMSGStatus, setUltraMSGStatus] = useState({});
  const [mainTab, setMainTab] = useState('empresas'); // 'empresas' o 'liquidaciones'
  
  // Respond.io state
  const [respondioStatus, setRespondioStatus] = useState({});
  const [savingRespondio, setSavingRespondio] = useState(false);
  
  // Liquidaciones state
  const [settlements, setSettlements] = useState([]);
  const [loadingSettlements, setLoadingSettlements] = useState(false);
  const [generatingSettlements, setGeneratingSettlements] = useState(false);
  const [settlementMonth, setSettlementMonth] = useState(new Date().getMonth() + 1);
  const [settlementYear, setSettlementYear] = useState(new Date().getFullYear());

  // Analytics state
  const [analyticsData, setAnalyticsData] = useState(null);
  const [analyticsSummary, setAnalyticsSummary] = useState(null);
  const [loadingAnalytics, setLoadingAnalytics] = useState(false);
  const [analyticsDays, setAnalyticsDays] = useState(30);
  const [analyticsFilterTenant, setAnalyticsFilterTenant] = useState('all');

  const [newTenant, setNewTenant] = useState({
    name: '',
    slug: '',
    legal_name: '',
    tax_id: '',
    fiscal_address: '',
    city: '',
    province: '',
    postal_code: '',
    phone: '',
    admin_email: '',
    additional_owners: [],
    currency: 'ARS',
    commission_type: 'fixed',
    commission_percentage: '',
    commission_tiers: [{ from_payments: 0, to_payments: 10000, amount_per_payment: 50 }]
  });

  const [newOwnerEmail, setNewOwnerEmail] = useState('');

  useEffect(() => {
    // If user data passed from AuthCallback, skip auth check
    if (location.state?.user) {
      loadTenants();
      return;
    }
    
    // Check authentication via API
    const checkAuth = async () => {
      try {
        const response = await api.get('/auth/me');
        const user = response.data;
        
        if (user.role !== 'superadmin') {
          navigate('/login');
          return;
        }
        loadTenants();
      } catch (error) {
        navigate('/login');
      } finally {
        setLoading(false);
      }
    };
    
    checkAuth();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [navigate, location.state]);

  const loadTenants = async () => {
    try {
      const params = {};
      if (filterActive === 'active') params.is_active = true;
      if (filterActive === 'inactive') params.is_active = false;
      
      const response = await tenantService.listTenants(params);
      setTenants(response.data);
    } catch (error) {
      toast.error('Error al cargar empresas');
    } finally {
      setLoading(false);
    }
  };

  // Reload when filter changes
  useEffect(() => {
    if (!loading) {
      loadTenants();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filterActive]);

  const verifyUltraMSG = async (tenantId) => {
    setVerifyingUltraMSG(tenantId);
    try {
      const response = await api.post(`/superadmin/tenants/${tenantId}/ultramsg/verify`);
      setUltraMSGStatus(prev => ({ ...prev, [tenantId]: response.data }));
      
      const { status, message } = response.data;
      
      if (status === 'connected') {
        toast.success('WhatsApp conectado correctamente');
      } else if (status === 'qr_needed') {
        toast.warning('Necesita escanear el código QR en UltraMSG');
      } else if (status === 'loading' || status === 'standby') {
        toast.info(message || 'Inicializando...');
      } else if (status === 'not_configured') {
        toast.info('UltraMSG no configurado');
      } else if (status === 'disconnected') {
        toast.warning(message || 'WhatsApp desconectado');
      } else {
        toast.error(message || 'Error de verificación');
      }
    } catch (error) {
      toast.error('Error al verificar conexión');
    } finally {
      setVerifyingUltraMSG(null);
    }
  };

  const addOwnerEmail = () => {
    if (!newOwnerEmail || !newOwnerEmail.includes('@')) {
      toast.error('Email inválido');
      return;
    }
    if (newTenant.additional_owners.includes(newOwnerEmail)) {
      toast.error('Email ya agregado');
      return;
    }
    setNewTenant({
      ...newTenant,
      additional_owners: [...newTenant.additional_owners, newOwnerEmail]
    });
    setNewOwnerEmail('');
  };

  const removeOwnerEmail = (email) => {
    setNewTenant({
      ...newTenant,
      additional_owners: newTenant.additional_owners.filter(e => e !== email)
    });
  };

  const addCommissionTier = () => {
    const lastTier = newTenant.commission_tiers[newTenant.commission_tiers.length - 1];
    setNewTenant({
      ...newTenant,
      commission_tiers: [
        ...newTenant.commission_tiers,
        {
          from_payments: (lastTier?.to_payments || 0) + 1,
          to_payments: null,
          amount_per_payment: lastTier?.amount_per_payment * 0.9 || 45
        }
      ]
    });
  };

  const updateCommissionTier = (index, field, value) => {
    const newTiers = [...newTenant.commission_tiers];
    newTiers[index] = { ...newTiers[index], [field]: value };
    setNewTenant({ ...newTenant, commission_tiers: newTiers });
  };

  const removeCommissionTier = (index) => {
    if (newTenant.commission_tiers.length <= 1) return;
    setNewTenant({
      ...newTenant,
      commission_tiers: newTenant.commission_tiers.filter((_, i) => i !== index)
    });
  };

  // Liquidaciones functions
  const loadSettlements = async () => {
    setLoadingSettlements(true);
    try {
      const response = await settlementService.listAll(settlementMonth, settlementYear);
      setSettlements(response.data);
    } catch (error) {
      console.error('Error loading settlements:', error);
      toast.error('Error al cargar liquidaciones');
    } finally {
      setLoadingSettlements(false);
    }
  };

  const generateSettlements = async () => {
    setGeneratingSettlements(true);
    try {
      const response = await settlementService.generate(settlementMonth, settlementYear);
      toast.success(`${response.data.generated_count} liquidaciones generadas`);
      loadSettlements();
    } catch (error) {
      toast.error('Error al generar liquidaciones');
    } finally {
      setGeneratingSettlements(false);
    }
  };

  const updateSettlementStatus = async (settlementId, newStatus) => {
    try {
      await settlementService.updateStatus(settlementId, newStatus);
      toast.success('Estado actualizado');
      loadSettlements();
    } catch (error) {
      toast.error('Error al actualizar estado');
    }
  };

  // Load settlements when tab changes or filters change
  useEffect(() => {
    if (mainTab === 'liquidaciones') {
      loadSettlements();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mainTab, settlementMonth, settlementYear]);

  // Analytics functions
  const loadAnalytics = async () => {
    setLoadingAnalytics(true);
    try {
      const tenantIdFilter = analyticsFilterTenant === 'all' ? null : analyticsFilterTenant;
      const [consultations, summary] = await Promise.all([
        analyticsService.getConsultations(analyticsDays, tenantIdFilter),
        analyticsService.getSummary()
      ]);
      setAnalyticsData(consultations.data);
      setAnalyticsSummary(summary.data);
    } catch (error) {
      console.error('Error loading analytics:', error);
      toast.error('Error al cargar analytics');
    } finally {
      setLoadingAnalytics(false);
    }
  };

  // Load analytics when tab changes or filters change
  useEffect(() => {
    if (mainTab === 'analytics') {
      loadAnalytics();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mainTab, analyticsDays, analyticsFilterTenant]);

  const monthNames = [
    'Enero', 'Febrero', 'Marzo', 'Abril', 'Mayo', 'Junio',
    'Julio', 'Agosto', 'Septiembre', 'Octubre', 'Noviembre', 'Diciembre'
  ];

  const handleCreateTenant = async () => {
    try {
      const payload = {
        ...newTenant,
        commission_percentage: newTenant.commission_type === 'percentage' 
          ? parseFloat(newTenant.commission_percentage) 
          : null,
        commission_tiers: newTenant.commission_type === 'fixed' 
          ? newTenant.commission_tiers.map(t => ({
              from_payments: parseInt(t.from_payments),
              to_payments: t.to_payments ? parseInt(t.to_payments) : null,
              amount_per_payment: parseFloat(t.amount_per_payment)
            }))
          : null
      };
      
      const response = await tenantService.createTenant(payload);
      const tenant = response.data;
      
      toast.success('Empresa creada exitosamente');
      
      // Show URLs info
      toast.info(
        `URL del Portal de Clientes: ${tenant.debtor_portal_url}`,
        { duration: 10000 }
      );
      
      setShowCreate(false);
      setNewTenant({ 
        name: '', slug: '', legal_name: '', tax_id: '', 
        fiscal_address: '', city: '', province: '', postal_code: '', 
        phone: '', admin_email: '', additional_owners: [], currency: 'ARS',
        commission_type: 'fixed', commission_percentage: '',
        commission_tiers: [{ from_payments: 0, to_payments: 10000, amount_per_payment: 50 }]
      });
      loadTenants();
    } catch (error) {
      const message = error.response?.data?.detail || 'Error al crear empresa';
      toast.error(message);
    }
  };

  const handleLogout = async () => {
    try {
      await api.post('/auth/logout');
    } catch (error) {
      console.error('Logout error:', error);
    }
    navigate('/login');
  };

  const handleEditTenant = async (tenantId) => {
    try {
      const response = await tenantService.getTenant(tenantId);
      setEditingTenant(response.data);
      setShowEdit(true);
    } catch (error) {
      toast.error('Error al cargar datos de la empresa');
    }
  };

  const handleUpdateTenant = async () => {
    try {
      await tenantService.updateTenant(editingTenant.id, {
        name: editingTenant.name,
        slug: editingTenant.slug,
        legal_name: editingTenant.legal_name,
        tax_id: editingTenant.tax_id,
        fiscal_address: editingTenant.fiscal_address,
        city: editingTenant.city,
        province: editingTenant.province,
        postal_code: editingTenant.postal_code,
        phone: editingTenant.phone,
        admin_email: editingTenant.admin_email,
        additional_owners: editingTenant.additional_owners || [],
        is_active: editingTenant.is_active
      });
      
      toast.success('Empresa actualizada exitosamente');
      setShowEdit(false);
      setEditingTenant(null);
      loadTenants();
    } catch (error) {
      toast.error('Error al actualizar empresa');
    }
  };

  const handleUpdateUltraMsg = async () => {
    try {
      await tenantService.updateUltraMsg(editingTenant.id, {
        instance_id: editingTenant.ultramsg_instance_id,
        token: editingTenant.ultramsg_token
      });
      
      toast.success('UltraMsg configurado exitosamente');
    } catch (error) {
      toast.error('Error al configurar UltraMsg');
    }
  };

  const handleUpdateRespondio = async () => {
    if (!editingTenant.respondio_token || !editingTenant.respondio_channel_id) {
      toast.error('Token y Channel ID son obligatorios');
      return;
    }
    
    setSavingRespondio(true);
    try {
      await tenantService.updateRespondio(editingTenant.id, {
        token: editingTenant.respondio_token,
        channel_id: editingTenant.respondio_channel_id,
        space_id: editingTenant.respondio_space_id || null
      });
      
      toast.success('Respond.io configurado exitosamente');
      // Update status
      const statusRes = await tenantService.getRespondioStatus(editingTenant.id);
      setRespondioStatus(prev => ({ ...prev, [editingTenant.id]: statusRes.data }));
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al configurar Respond.io');
    } finally {
      setSavingRespondio(false);
    }
  };

  const loadRespondioStatus = async (tenantId) => {
    try {
      const response = await tenantService.getRespondioStatus(tenantId);
      setRespondioStatus(prev => ({ ...prev, [tenantId]: response.data }));
    } catch (error) {
      console.error('Error loading Respond.io status:', error);
    }
  };

  const handleUpdateCommission = async () => {
    try {
      const data = {
        commission_type: editingTenant.commission_type || 'fixed',
        commission_percentage: editingTenant.commission_type === 'percentage' 
          ? parseFloat(editingTenant.commission_percentage) 
          : null,
        commission_tiers: editingTenant.commission_type === 'fixed' 
          ? (editingTenant.commission_tiers || []).map(t => ({
              from_payments: parseInt(t.from_payments) || 0,
              to_payments: t.to_payments ? parseInt(t.to_payments) : null,
              amount_per_payment: parseFloat(t.amount_per_payment) || 0
            }))
          : null
      };
      
      await tenantService.updateCommission(editingTenant.id, data);
      toast.success('Configuración de comisión actualizada');
      loadTenants();
    } catch (error) {
      toast.error('Error al actualizar configuración de comisión');
    }
  };

  const addEditTier = () => {
    const tiers = editingTenant.commission_tiers || [];
    const lastTier = tiers[tiers.length - 1];
    const newFrom = lastTier ? (lastTier.to_payments || lastTier.from_payments) + 1 : 0;
    setEditingTenant({
      ...editingTenant,
      commission_tiers: [
        ...tiers,
        { from_payments: newFrom, to_payments: null, amount_per_payment: 0 }
      ]
    });
  };

  const updateEditTier = (idx, field, value) => {
    const tiers = [...(editingTenant.commission_tiers || [])];
    tiers[idx] = { ...tiers[idx], [field]: value === '' ? null : value };
    setEditingTenant({ ...editingTenant, commission_tiers: tiers });
  };

  const removeEditTier = (idx) => {
    if ((editingTenant.commission_tiers || []).length <= 1) return;
    setEditingTenant({
      ...editingTenant,
      commission_tiers: editingTenant.commission_tiers.filter((_, i) => i !== idx)
    });
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <div className="skeleton w-96 h-96 rounded-xl"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Header */}
      <div className="bg-white border-b border-slate-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <div className="flex items-center gap-2">
              <div className="w-10 h-10 bg-slate-900 rounded-lg flex items-center justify-center">
                <TrendingUp className="w-6 h-6 text-teal-500" />
              </div>
              <span className="text-xl font-bold text-slate-900" style={{fontFamily: 'Manrope'}}>MacroPago</span>
              <span className="ml-2 px-2 py-1 bg-teal-100 text-teal-700 text-xs font-semibold rounded">SUPERADMIN</span>
            </div>
            <Button variant="ghost" onClick={handleLogout} data-testid="logout-button">
              <LogOut className="w-5 h-5" />
            </Button>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8" data-testid="superadmin-dashboard">
        {/* Main Tabs */}
        <Tabs value={mainTab} onValueChange={setMainTab} className="w-full">
          <TabsList className="mb-6">
            <TabsTrigger value="empresas" className="data-[state=active]:bg-white">
              <Building2 className="w-4 h-4 mr-2" />
              Empresas
            </TabsTrigger>
            <TabsTrigger value="liquidaciones" className="data-[state=active]:bg-white">
              <DollarSign className="w-4 h-4 mr-2" />
              Liquidaciones
            </TabsTrigger>
            <TabsTrigger value="analytics" className="data-[state=active]:bg-white">
              <BarChart3 className="w-4 h-4 mr-2" />
              Analytics
            </TabsTrigger>
          </TabsList>

          {/* Tab: Empresas */}
          <TabsContent value="empresas">
            <div className="flex justify-between items-center mb-8">
              <div>
                <h1 className="text-3xl font-bold text-slate-900" style={{fontFamily: 'Manrope'}}>
                  Gestión de Empresas
                </h1>
                <p className="text-slate-600 mt-1">
                  {tenants.length} empresa{tenants.length !== 1 ? 's' : ''} 
                  {filterActive !== 'all' && ` (${filterActive === 'active' ? 'activas' : 'inactivas'})`}
                </p>
              </div>
              
              <div className="flex gap-3">
                {/* Filter */}
                <Select value={filterActive} onValueChange={setFilterActive}>
                  <SelectTrigger className="w-[160px]" data-testid="filter-status">
                    <SelectValue placeholder="Filtrar" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Todas</SelectItem>
                    <SelectItem value="active">Activas</SelectItem>
                    <SelectItem value="inactive">Inactivas</SelectItem>
                  </SelectContent>
                </Select>
                
                <Dialog open={showCreate} onOpenChange={setShowCreate}>
                  <DialogTrigger asChild>
                    <Button className="bg-slate-900 hover:bg-slate-800" data-testid="create-tenant-button">
                      <Plus className="w-5 h-5 mr-2" />
                      Nueva Empresa
                    </Button>
                  </DialogTrigger>
                  <DialogContent data-testid="create-tenant-dialog" className="max-w-2xl max-h-[90vh] overflow-y-auto">
                    <DialogHeader>
                      <DialogTitle>Crear Nueva Empresa</DialogTitle>
                      <DialogDescription>
                        Configure los datos de la empresa y su modelo de comisión
                      </DialogDescription>
                    </DialogHeader>
                    <Tabs defaultValue="basicos" className="w-full mt-4">
                      <TabsList className="grid w-full grid-cols-3">
                        <TabsTrigger value="basicos">Datos Básicos</TabsTrigger>
                        <TabsTrigger value="duenos">Dueños</TabsTrigger>
                    <TabsTrigger value="comision">Comisión</TabsTrigger>
                  </TabsList>
                  
                  <TabsContent value="basicos" className="space-y-4 mt-4">
                    {/* Datos básicos */}
                    <div className="bg-slate-50 p-4 rounded-lg">
                      <h3 className="font-semibold text-slate-900 mb-3">Datos Básicos</h3>
                      <div className="space-y-3">
                        <div>
                          <Label>Nombre Comercial *</Label>
                          <Input
                            data-testid="tenant-name-input"
                            value={newTenant.name}
                            onChange={(e) => setNewTenant({...newTenant, name: e.target.value})}
                            placeholder="Cobranzas del Sur SA"
                          />
                        </div>
                        <div>
                          <Label>URL Personalizada (slug) *</Label>
                          <Input
                            data-testid="tenant-slug-input"
                            value={newTenant.slug}
                            onChange={(e) => setNewTenant({...newTenant, slug: e.target.value.toLowerCase()})}
                            placeholder="cobranzas-sur"
                          />
                          <p className="text-xs text-slate-500 mt-1">
                            Solo letras, números y guiones. Ej: tu-empresa
                          </p>
                        </div>
                      </div>
                    </div>

                    {/* Datos fiscales */}
                    <div className="bg-slate-50 p-4 rounded-lg">
                      <h3 className="font-semibold text-slate-900 mb-3">Datos Fiscales</h3>
                      <div className="space-y-3">
                        <div>
                          <Label>Razón Social *</Label>
                          <Input
                            data-testid="tenant-legal-name-input"
                            value={newTenant.legal_name}
                            onChange={(e) => setNewTenant({...newTenant, legal_name: e.target.value})}
                            placeholder="Cobranzas del Sur Sociedad Anónima"
                          />
                        </div>
                        <div>
                          <Label>CUIT/CUIL *</Label>
                          <Input
                            data-testid="tenant-tax-id-input"
                            value={newTenant.tax_id}
                            onChange={(e) => setNewTenant({...newTenant, tax_id: e.target.value})}
                            placeholder="30-12345678-9"
                          />
                        </div>
                        <div>
                          <Label>Dirección Fiscal</Label>
                          <Input
                            data-testid="tenant-fiscal-address-input"
                            value={newTenant.fiscal_address}
                            onChange={(e) => setNewTenant({...newTenant, fiscal_address: e.target.value})}
                            placeholder="Av. Corrientes 1234"
                          />
                        </div>
                        <div className="grid grid-cols-3 gap-2">
                          <div>
                            <Label>Ciudad</Label>
                            <Input
                              value={newTenant.city}
                              onChange={(e) => setNewTenant({...newTenant, city: e.target.value})}
                              placeholder="CABA"
                            />
                          </div>
                          <div>
                            <Label>Provincia</Label>
                            <Input
                              value={newTenant.province}
                              onChange={(e) => setNewTenant({...newTenant, province: e.target.value})}
                              placeholder="Buenos Aires"
                            />
                          </div>
                          <div>
                            <Label>CP</Label>
                            <Input
                              value={newTenant.postal_code}
                              onChange={(e) => setNewTenant({...newTenant, postal_code: e.target.value})}
                              placeholder="1000"
                            />
                          </div>
                        </div>
                        <div>
                          <Label>Teléfono</Label>
                          <Input
                            value={newTenant.phone}
                            onChange={(e) => setNewTenant({...newTenant, phone: e.target.value})}
                            placeholder="+54 11 1234-5678"
                          />
                        </div>
                      </div>
                    </div>
                    
                    {/* Email del dueño principal - En datos básicos por ser obligatorio */}
                    <div className="bg-blue-50 p-4 rounded-lg border border-blue-200">
                      <h3 className="font-semibold text-slate-900 mb-2">Email del Dueno Principal *</h3>
                      <p className="text-xs text-slate-600 mb-3">
                        Podra acceder al panel de gestion con su cuenta de Google
                      </p>
                      <Input
                        data-testid="tenant-admin-email-input-main"
                        type="email"
                        value={newTenant.admin_email}
                        onChange={(e) => setNewTenant({...newTenant, admin_email: e.target.value})}
                        placeholder="dueno@gmail.com"
                      />
                    </div>
                  </TabsContent>

                  <TabsContent value="duenos" className="space-y-4 mt-4">
                    {/* Email del dueño principal */}
                    <div className="bg-teal-50 p-4 rounded-lg border border-teal-200">
                      <h3 className="font-semibold text-slate-900 mb-2">Dueño Principal</h3>
                      <p className="text-xs text-slate-600 mb-3">
                        Podrá acceder al panel de gestión con su cuenta de Google
                      </p>
                      <div>
                        <Label>Email del Dueño (Gmail) *</Label>
                        <Input
                          data-testid="tenant-admin-email-input"
                          type="email"
                          value={newTenant.admin_email}
                          onChange={(e) => setNewTenant({...newTenant, admin_email: e.target.value})}
                          placeholder="dueño@gmail.com"
                        />
                      </div>
                    </div>

                    {/* Dueños adicionales */}
                    <div className="bg-slate-50 p-4 rounded-lg">
                      <h3 className="font-semibold text-slate-900 mb-2">Dueños Adicionales</h3>
                      <p className="text-xs text-slate-600 mb-3">
                        Agrega más personas que puedan administrar la empresa
                      </p>
                      
                      <div className="flex gap-2 mb-3">
                        <Input
                          type="email"
                          value={newOwnerEmail}
                          onChange={(e) => setNewOwnerEmail(e.target.value)}
                          placeholder="otro-dueño@gmail.com"
                          onKeyDown={(e) => e.key === 'Enter' && addOwnerEmail()}
                        />
                        <Button type="button" onClick={addOwnerEmail} variant="outline">
                          <Plus className="w-4 h-4" />
                        </Button>
                      </div>

                      {newTenant.additional_owners.length > 0 && (
                        <div className="space-y-2">
                          {newTenant.additional_owners.map((email) => (
                            <div key={email} className="flex items-center justify-between bg-white p-2 rounded border">
                              <div className="flex items-center gap-2">
                                <Users className="w-4 h-4 text-slate-400" />
                                <span className="text-sm">{email}</span>
                              </div>
                              <Button
                                size="sm"
                                variant="ghost"
                                onClick={() => removeOwnerEmail(email)}
                                className="text-red-600 hover:bg-red-50"
                              >
                                <X className="w-4 h-4" />
                              </Button>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  </TabsContent>

                  <TabsContent value="comision" className="space-y-4 mt-4">
                    <div className="bg-amber-50 p-4 rounded-lg border border-amber-200">
                      <h3 className="font-semibold text-slate-900 mb-2 flex items-center gap-2">
                        <DollarSign className="w-5 h-5 text-amber-600" />
                        Comisión del Servicio
                      </h3>
                      <p className="text-xs text-slate-600">
                        Define cómo se cobrará a esta empresa por el uso del servicio
                      </p>
                    </div>

                    <div className="space-y-4">
                      <div>
                        <Label>Tipo de Comisión</Label>
                        <Select
                          value={newTenant.commission_type}
                          onValueChange={(value) => setNewTenant({...newTenant, commission_type: value})}
                        >
                          <SelectTrigger data-testid="commission-type-select">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="fixed">Monto Fijo por Pago (Escalonado)</SelectItem>
                            <SelectItem value="percentage">Porcentaje del Recupero</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>

                      {newTenant.commission_type === 'percentage' ? (
                        <div className="bg-slate-50 p-4 rounded-lg">
                          <Label>Porcentaje de Comisión (%)</Label>
                          <Input
                            type="number"
                            min="0"
                            max="100"
                            step="0.1"
                            value={newTenant.commission_percentage}
                            onChange={(e) => setNewTenant({...newTenant, commission_percentage: e.target.value})}
                            placeholder="10"
                          />
                          <p className="text-xs text-slate-500 mt-1">
                            Ej: 10% significa que por cada $100 recuperados, se cobra $10
                          </p>
                        </div>
                      ) : (
                        <div className="bg-slate-50 p-4 rounded-lg space-y-3">
                          <div className="flex justify-between items-center">
                            <Label>Tramos de Comisión</Label>
                            <Button size="sm" variant="outline" onClick={addCommissionTier}>
                              <Plus className="w-4 h-4 mr-1" /> Agregar Tramo
                            </Button>
                          </div>
                          
                          {newTenant.commission_tiers.map((tier, idx) => (
                            <div key={`tier-${tier.from_payments}-${tier.percentage}`} className="grid grid-cols-4 gap-2 items-end bg-white p-3 rounded border">
                              <div>
                                <Label className="text-xs">Desde pagos</Label>
                                <Input
                                  type="number"
                                  value={tier.from_payments}
                                  onChange={(e) => updateCommissionTier(idx, 'from_payments', e.target.value)}
                                />
                              </div>
                              <div>
                                <Label className="text-xs">Hasta pagos</Label>
                                <Input
                                  type="number"
                                  value={tier.to_payments || ''}
                                  onChange={(e) => updateCommissionTier(idx, 'to_payments', e.target.value || null)}
                                  placeholder="∞"
                                />
                              </div>
                              <div>
                                <Label className="text-xs">$ por pago</Label>
                                <Input
                                  type="number"
                                  step="0.01"
                                  value={tier.amount_per_payment}
                                  onChange={(e) => updateCommissionTier(idx, 'amount_per_payment', e.target.value)}
                                />
                              </div>
                              <Button
                                size="sm"
                                variant="ghost"
                                onClick={() => removeCommissionTier(idx)}
                                disabled={newTenant.commission_tiers.length <= 1}
                                className="text-red-600 hover:bg-red-50"
                              >
                                <Trash2 className="w-4 h-4" />
                              </Button>
                            </div>
                          ))}
                          <p className="text-xs text-slate-500">
                            Ej: 0-10,000 pagos = $50/pago, 10,001+ = $45/pago
                          </p>
                        </div>
                      )}
                    </div>
                  </TabsContent>
                </Tabs>

                {(() => {
                  const missing = [];
                  if (!newTenant.name) missing.push('Nombre');
                  if (!newTenant.slug) missing.push('URL');
                  if (!newTenant.legal_name) missing.push('Razon Social');
                  if (!newTenant.tax_id) missing.push('CUIT');
                  if (!newTenant.admin_email) missing.push('Email del Dueno');
                  return missing.length > 0 ? (
                    <p className="text-xs text-amber-600 mt-3 text-center">
                      Campos obligatorios faltantes: {missing.join(', ')}
                    </p>
                  ) : null;
                })()}

                <Button
                  data-testid="submit-create-tenant-button"
                  onClick={handleCreateTenant}
                  className="w-full mt-3"
                  style={!newTenant.name || !newTenant.slug || !newTenant.legal_name || !newTenant.tax_id || !newTenant.admin_email ? {} : { backgroundColor: '#1e3a5f' }}
                  disabled={!newTenant.name || !newTenant.slug || !newTenant.legal_name || !newTenant.tax_id || !newTenant.admin_email}
                >
                  Crear Empresa
                </Button>
              </DialogContent>
            </Dialog>
          </div>
        </div>

        {/* Tenants Grid */}
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
          {tenants.map((tenant, index) => (
            <motion.div
              key={tenant.id}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: index * 0.1 }}
            >
              <Card className="p-6 border-slate-200 bg-white card-hover" data-testid={`tenant-card-${tenant.id}`}>
                <div className="flex items-start justify-between mb-4">
                  <div className="w-12 h-12 bg-slate-100 rounded-lg flex items-center justify-center">
                    <Building2 className="w-6 h-6 text-slate-600" />
                  </div>
                  <span className={`px-2 py-1 text-xs font-semibold rounded ${
                    tenant.is_active ? 'bg-emerald-100 text-emerald-700' : 'bg-slate-100 text-slate-600'
                  }`}>
                    {tenant.is_active ? 'Activa' : 'Inactiva'}
                  </span>
                </div>
                
                <h3 className="text-lg font-semibold text-slate-900 mb-1" style={{fontFamily: 'Manrope'}}>
                  {tenant.name}
                </h3>
                {tenant.legal_name && (
                  <p className="text-xs text-slate-500 mb-2">{tenant.legal_name}</p>
                )}
                
                <div className="space-y-2 text-sm mb-4">
                  {tenant.tax_id && (
                    <div className="flex items-center justify-between">
                      <span className="text-slate-600">CUIT:</span>
                      <span className="font-medium text-slate-900">{tenant.tax_id}</span>
                    </div>
                  )}
                  
                  {/* Comisión */}
                  <div className="flex items-center justify-between">
                    <span className="text-slate-600">Comisión:</span>
                    <Badge variant="outline" className="text-xs">
                      {tenant.commission_type === 'percentage' 
                        ? `${tenant.commission_percentage || 0}% del recupero`
                        : 'Monto fijo escalonado'}
                    </Badge>
                  </div>
                  
                  {/* UltraMSG Status */}
                  <div className="flex items-center justify-between">
                    <span className="text-slate-600">UltraMSG:</span>
                    <div className="flex items-center gap-1">
                      {tenant.ultramsg_status === 'not_configured' ? (
                        <Badge variant="outline" className="text-slate-500">
                          <WifiOff className="w-3 h-3 mr-1" /> No configurado
                        </Badge>
                      ) : ultraMSGStatus[tenant.id]?.status === 'connected' ? (
                        <Badge className="bg-emerald-100 text-emerald-700">
                          <Wifi className="w-3 h-3 mr-1" /> Conectado
                        </Badge>
                      ) : ultraMSGStatus[tenant.id]?.status === 'disconnected' ? (
                        <Badge className="bg-amber-100 text-amber-700">
                          <WifiOff className="w-3 h-3 mr-1" /> Desconectado
                        </Badge>
                      ) : (
                        <Button
                          size="sm"
                          variant="ghost"
                          className="h-6 text-xs"
                          onClick={() => verifyUltraMSG(tenant.id)}
                          disabled={verifyingUltraMSG === tenant.id}
                        >
                          {verifyingUltraMSG === tenant.id ? (
                            <RefreshCw className="w-3 h-3 animate-spin" />
                          ) : (
                            <>Verificar</>
                          )}
                        </Button>
                      )}
                    </div>
                  </div>
                  
                  {/* Dueños */}
                  <div className="border-t border-slate-100 pt-2 mt-2">
                    <div className="flex items-center justify-between mb-1">
                      <span className="text-slate-600 text-xs font-medium">Dueños ({1 + (tenant.additional_owners?.length || 0)})</span>
                    </div>
                    <div className="space-y-1">
                      <div className="flex items-center gap-1 text-xs">
                        <Badge variant="outline" className="text-xs bg-teal-50 text-teal-700 border-teal-200">
                          Principal
                        </Badge>
                        <span className="text-slate-700 truncate" title={tenant.admin_email}>
                          {tenant.admin_email}
                        </span>
                      </div>
                      {tenant.additional_owners?.map((email) => (
                        <div key={email} className="flex items-center gap-1 text-xs">
                          <Users className="w-3 h-3 text-slate-400" />
                          <span className="text-slate-600 truncate" title={email}>{email}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
                
                {/* URLs */}
                <div className="border-t border-slate-200 pt-4 space-y-2">
                  <div>
                    <p className="text-xs text-slate-600 mb-1">Portal de Clientes:</p>
                    <div className="flex items-center gap-2">
                      <code className="text-xs bg-slate-100 text-teal-700 px-2 py-1 rounded flex-1 truncate">
                        /t/{tenant.slug}
                      </code>
                      <button
                        onClick={() => {
                          navigator.clipboard.writeText(tenant.debtor_portal_url || `${window.location.origin}/t/${tenant.slug}`);
                          toast.success('URL copiada');
                        }}
                        className="text-teal-600 hover:text-teal-700 text-xs font-medium"
                      >
                        Copiar
                      </button>
                    </div>
                  </div>
                  <p className="text-xs text-slate-500">
                    Creada: {new Date(tenant.created_at).toLocaleDateString('es-AR')}
                  </p>
                  
                  {/* Edit button */}
                  <button
                    onClick={() => handleEditTenant(tenant.id)}
                    className="w-full mt-2 px-3 py-2 bg-slate-900 text-white rounded-lg hover:bg-slate-800 text-sm font-medium flex items-center justify-center gap-2"
                    data-testid={`edit-tenant-${tenant.id}`}
                  >
                    <Edit2 className="w-4 h-4" />
                    Editar Empresa
                  </button>
                </div>
              </Card>
            </motion.div>
          ))}
        </div>

        {tenants.length === 0 && (
          <div className="text-center py-12">
            <Building2 className="w-16 h-16 text-slate-300 mx-auto mb-4" />
            <h3 className="text-lg font-semibold text-slate-900 mb-2" style={{fontFamily: 'Manrope'}}>
              {filterActive === 'all' ? 'No hay empresas creadas' : `No hay empresas ${filterActive === 'active' ? 'activas' : 'inactivas'}`}
            </h3>
            <p className="text-slate-600 mb-4">
              {filterActive === 'all' ? 'Crea tu primera empresa para comenzar' : 'Cambia el filtro para ver otras empresas'}
            </p>
          </div>
        )}
          </TabsContent>

          {/* Tab: Liquidaciones */}
          <TabsContent value="liquidaciones">
            <div className="space-y-6">
              {/* Header */}
              <div className="flex justify-between items-center">
                <div>
                  <h1 className="text-3xl font-bold text-slate-900" style={{fontFamily: 'Manrope'}}>
                    Liquidaciones Mensuales
                  </h1>
                  <p className="text-slate-600 mt-1">
                    Gestiona las comisiones a cobrar a cada empresa
                  </p>
                </div>
                
                <div className="flex gap-3 items-center">
                  {/* Month selector */}
                  <Select value={settlementMonth.toString()} onValueChange={(v) => setSettlementMonth(parseInt(v))}>
                    <SelectTrigger className="w-[140px]">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {monthNames.map((name, idx) => (
                        <SelectItem key={name} value={(idx + 1).toString()}>{name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  
                  {/* Year selector */}
                  <Select value={settlementYear.toString()} onValueChange={(v) => setSettlementYear(parseInt(v))}>
                    <SelectTrigger className="w-[100px]">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {[2024, 2025, 2026].map((year) => (
                        <SelectItem key={year} value={year.toString()}>{year}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  
                  <Button
                    onClick={generateSettlements}
                    disabled={generatingSettlements}
                    className="bg-teal-600 hover:bg-teal-700"
                  >
                    {generatingSettlements ? (
                      <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                    ) : (
                      <Plus className="w-4 h-4 mr-2" />
                    )}
                    Generar Liquidaciones
                  </Button>
                </div>
              </div>

              {/* Summary Cards */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <Card className="p-4 border-slate-200 bg-white">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-slate-600">Total a Cobrar</p>
                      <p className="text-2xl font-bold text-slate-900">
                        ${settlements.reduce((sum, s) => sum + s.commission_amount, 0).toLocaleString('es-AR', {minimumFractionDigits: 2})}
                      </p>
                    </div>
                    <DollarSign className="w-8 h-8 text-teal-600" />
                  </div>
                </Card>
                
                <Card className="p-4 border-slate-200 bg-white">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-slate-600">Total Recuperado</p>
                      <p className="text-2xl font-bold text-slate-900">
                        ${settlements.reduce((sum, s) => sum + s.total_recovered, 0).toLocaleString('es-AR', {minimumFractionDigits: 2})}
                      </p>
                    </div>
                    <TrendingUp className="w-8 h-8 text-emerald-600" />
                  </div>
                </Card>
                
                <Card className="p-4 border-slate-200 bg-white">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-slate-600">Pagos Procesados</p>
                      <p className="text-2xl font-bold text-slate-900">
                        {settlements.reduce((sum, s) => sum + s.total_payments, 0).toLocaleString('es-AR')}
                      </p>
                    </div>
                    <Users className="w-8 h-8 text-blue-600" />
                  </div>
                </Card>
              </div>

              {/* Settlements Table */}
              <Card className="border-slate-200 bg-white overflow-hidden">
                <div className="p-4 border-b border-slate-200">
                  <h2 className="text-lg font-semibold text-slate-900">
                    Liquidaciones de {monthNames[settlementMonth - 1]} {settlementYear}
                  </h2>
                </div>
                
                {loadingSettlements ? (
                  <div className="p-12 text-center">
                    <RefreshCw className="w-8 h-8 text-slate-400 mx-auto animate-spin" />
                  </div>
                ) : settlements.length === 0 ? (
                  <div className="p-12 text-center">
                    <DollarSign className="w-12 h-12 text-slate-300 mx-auto mb-4" />
                    <p className="text-slate-600">No hay liquidaciones para este período</p>
                    <p className="text-sm text-slate-500 mt-1">
                      Haz clic en "Generar Liquidaciones" para crearlas
                    </p>
                  </div>
                ) : (
                  <div className="overflow-x-auto">
                    <table className="w-full">
                      <thead className="bg-slate-50">
                        <tr>
                          <th className="px-4 py-3 text-left text-xs font-medium text-slate-600 uppercase">Empresa</th>
                          <th className="px-4 py-3 text-left text-xs font-medium text-slate-600 uppercase">Pagos</th>
                          <th className="px-4 py-3 text-left text-xs font-medium text-slate-600 uppercase">Recuperado</th>
                          <th className="px-4 py-3 text-left text-xs font-medium text-slate-600 uppercase">Tipo</th>
                          <th className="px-4 py-3 text-left text-xs font-medium text-slate-600 uppercase">Comisión</th>
                          <th className="px-4 py-3 text-left text-xs font-medium text-slate-600 uppercase">Estado</th>
                          <th className="px-4 py-3 text-left text-xs font-medium text-slate-600 uppercase">Acciones</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-slate-200">
                        {settlements.map((settlement) => (
                          <tr key={settlement.id} className="hover:bg-slate-50">
                            <td className="px-4 py-4">
                              <div>
                                <p className="font-medium text-slate-900">{settlement.tenant_name}</p>
                                <p className="text-xs text-slate-500">{settlement.tenant_slug}</p>
                              </div>
                            </td>
                            <td className="px-4 py-4 text-sm text-slate-900">
                              {settlement.total_payments.toLocaleString('es-AR')}
                            </td>
                            <td className="px-4 py-4 text-sm font-medium text-slate-900">
                              ${settlement.total_recovered.toLocaleString('es-AR', {minimumFractionDigits: 2})}
                            </td>
                            <td className="px-4 py-4">
                              <Badge variant="outline" className="text-xs">
                                {settlement.commission_type === 'percentage' ? (
                                  <><Percent className="w-3 h-3 mr-1" /> Porcentaje</>
                                ) : (
                                  'Fijo'
                                )}
                              </Badge>
                            </td>
                            <td className="px-4 py-4 text-sm font-bold text-teal-600">
                              ${settlement.commission_amount.toLocaleString('es-AR', {minimumFractionDigits: 2})}
                            </td>
                            <td className="px-4 py-4">
                              <Badge className={
                                settlement.status === 'paid' ? 'bg-emerald-100 text-emerald-700' :
                                settlement.status === 'invoiced' ? 'bg-blue-100 text-blue-700' :
                                'bg-amber-100 text-amber-700'
                              }>
                                {settlement.status === 'paid' ? 'Cobrada' :
                                 settlement.status === 'invoiced' ? 'Facturada' :
                                 'Pendiente'}
                              </Badge>
                            </td>
                            <td className="px-4 py-4">
                              <Select
                                value={settlement.status}
                                onValueChange={(v) => updateSettlementStatus(settlement.id, v)}
                              >
                                <SelectTrigger className="w-[120px] h-8 text-xs">
                                  <SelectValue />
                                </SelectTrigger>
                                <SelectContent>
                                  <SelectItem value="pending">Pendiente</SelectItem>
                                  <SelectItem value="invoiced">Facturada</SelectItem>
                                  <SelectItem value="paid">Cobrada</SelectItem>
                                </SelectContent>
                              </Select>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </Card>
            </div>
          </TabsContent>

          {/* Tab: Analytics */}
          <TabsContent value="analytics">
            <div className="space-y-6">
              {/* Header */}
              <div className="flex justify-between items-center flex-wrap gap-4">
                <div>
                  <h1 className="text-3xl font-bold text-slate-900" style={{fontFamily: 'Manrope'}}>
                    Analytics de Consultas
                  </h1>
                  <p className="text-slate-600 mt-1">
                    Métricas de uso del portal de clientes
                  </p>
                </div>
                
                <div className="flex gap-3 items-center flex-wrap">
                  <Select value={analyticsFilterTenant} onValueChange={setAnalyticsFilterTenant}>
                    <SelectTrigger className="w-[200px]">
                      <SelectValue placeholder="Todas las empresas" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">Todas las empresas</SelectItem>
                      {tenants.map(t => (
                        <SelectItem key={t.id} value={t.id}>{t.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  
                  <Select value={analyticsDays.toString()} onValueChange={(v) => setAnalyticsDays(parseInt(v))}>
                    <SelectTrigger className="w-[160px]">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="7">Últimos 7 días</SelectItem>
                      <SelectItem value="30">Últimos 30 días</SelectItem>
                      <SelectItem value="90">Últimos 90 días</SelectItem>
                    </SelectContent>
                  </Select>
                  
                  <Button
                    onClick={loadAnalytics}
                    disabled={loadingAnalytics}
                    variant="outline"
                  >
                    {loadingAnalytics ? (
                      <RefreshCw className="w-4 h-4 animate-spin" />
                    ) : (
                      <RefreshCw className="w-4 h-4" />
                    )}
                  </Button>
                </div>
              </div>

              {/* Summary Cards */}
              <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                <Card className="p-4 border-slate-200 bg-white">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-slate-600">Hoy</p>
                      <p className="text-2xl font-bold text-slate-900">
                        {analyticsSummary?.today?.toLocaleString('es-AR') || 0}
                      </p>
                    </div>
                    <div className="w-10 h-10 bg-teal-100 rounded-full flex items-center justify-center">
                      <BarChart3 className="w-5 h-5 text-teal-600" />
                    </div>
                  </div>
                </Card>
                
                <Card className="p-4 border-slate-200 bg-white">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-slate-600">Esta Semana</p>
                      <p className="text-2xl font-bold text-slate-900">
                        {analyticsSummary?.this_week?.toLocaleString('es-AR') || 0}
                      </p>
                    </div>
                    <div className="w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center">
                      <TrendingUp className="w-5 h-5 text-blue-600" />
                    </div>
                  </div>
                </Card>
                
                <Card className="p-4 border-slate-200 bg-white">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-slate-600">Este Mes</p>
                      <p className="text-2xl font-bold text-slate-900">
                        {analyticsSummary?.this_month?.toLocaleString('es-AR') || 0}
                      </p>
                    </div>
                    <div className="w-10 h-10 bg-emerald-100 rounded-full flex items-center justify-center">
                      <Users className="w-5 h-5 text-emerald-600" />
                    </div>
                  </div>
                </Card>
                
                <Card className="p-4 border-slate-200 bg-white">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-slate-600">% Móvil</p>
                      <p className="text-2xl font-bold text-slate-900">
                        {analyticsSummary?.mobile_percentage || 0}%
                      </p>
                    </div>
                    <div className="w-10 h-10 bg-violet-100 rounded-full flex items-center justify-center">
                      <Smartphone className="w-5 h-5 text-violet-600" />
                    </div>
                  </div>
                </Card>
              </div>

              {loadingAnalytics ? (
                <div className="p-12 text-center">
                  <RefreshCw className="w-8 h-8 text-slate-400 mx-auto animate-spin" />
                  <p className="text-slate-600 mt-2">Cargando analytics...</p>
                </div>
              ) : analyticsData ? (
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                  {/* Device Breakdown Card */}
                  <Card className="p-6 border-slate-200 bg-white">
                    <h3 className="text-lg font-semibold text-slate-900 mb-4 flex items-center gap-2">
                      <Monitor className="w-5 h-5" />
                      Consultas por Dispositivo
                    </h3>
                    <div className="space-y-4">
                      {/* Mobile */}
                      <div className="space-y-2">
                        <div className="flex justify-between text-sm">
                          <span className="flex items-center gap-2">
                            <Smartphone className="w-4 h-4 text-violet-600" />
                            Móvil
                          </span>
                          <span className="font-medium">
                            {analyticsData.device_breakdown?.mobile || 0}
                            <span className="text-slate-500 ml-1">
                              ({analyticsData.total_consultations > 0 
                                ? Math.round((analyticsData.device_breakdown?.mobile || 0) / analyticsData.total_consultations * 100) 
                                : 0}%)
                            </span>
                          </span>
                        </div>
                        <div className="w-full bg-slate-100 rounded-full h-3">
                          <div 
                            className="bg-violet-600 h-3 rounded-full transition-all"
                            style={{ 
                              width: `${analyticsData.total_consultations > 0 
                                ? (analyticsData.device_breakdown?.mobile || 0) / analyticsData.total_consultations * 100 
                                : 0}%` 
                            }}
                          />
                        </div>
                      </div>
                      
                      {/* Desktop */}
                      <div className="space-y-2">
                        <div className="flex justify-between text-sm">
                          <span className="flex items-center gap-2">
                            <Monitor className="w-4 h-4 text-blue-600" />
                            Desktop
                          </span>
                          <span className="font-medium">
                            {analyticsData.device_breakdown?.desktop || 0}
                            <span className="text-slate-500 ml-1">
                              ({analyticsData.total_consultations > 0 
                                ? Math.round((analyticsData.device_breakdown?.desktop || 0) / analyticsData.total_consultations * 100) 
                                : 0}%)
                            </span>
                          </span>
                        </div>
                        <div className="w-full bg-slate-100 rounded-full h-3">
                          <div 
                            className="bg-blue-600 h-3 rounded-full transition-all"
                            style={{ 
                              width: `${analyticsData.total_consultations > 0 
                                ? (analyticsData.device_breakdown?.desktop || 0) / analyticsData.total_consultations * 100 
                                : 0}%` 
                            }}
                          />
                        </div>
                      </div>
                      
                      {/* Tablet */}
                      <div className="space-y-2">
                        <div className="flex justify-between text-sm">
                          <span className="flex items-center gap-2">
                            <Tablet className="w-4 h-4 text-teal-600" />
                            Tablet
                          </span>
                          <span className="font-medium">
                            {analyticsData.device_breakdown?.tablet || 0}
                            <span className="text-slate-500 ml-1">
                              ({analyticsData.total_consultations > 0 
                                ? Math.round((analyticsData.device_breakdown?.tablet || 0) / analyticsData.total_consultations * 100) 
                                : 0}%)
                            </span>
                          </span>
                        </div>
                        <div className="w-full bg-slate-100 rounded-full h-3">
                          <div 
                            className="bg-teal-600 h-3 rounded-full transition-all"
                            style={{ 
                              width: `${analyticsData.total_consultations > 0 
                                ? (analyticsData.device_breakdown?.tablet || 0) / analyticsData.total_consultations * 100 
                                : 0}%` 
                            }}
                          />
                        </div>
                      </div>
                    </div>
                    
                    <div className="mt-6 pt-4 border-t border-slate-100">
                      <div className="text-center">
                        <p className="text-3xl font-bold text-slate-900">{analyticsData.total_consultations?.toLocaleString('es-AR')}</p>
                        <p className="text-sm text-slate-600">consultas totales</p>
                      </div>
                    </div>
                  </Card>

                  {/* OS Breakdown Card */}
                  <Card className="p-6 border-slate-200 bg-white">
                    <h3 className="text-lg font-semibold text-slate-900 mb-4 flex items-center gap-2">
                      <Settings className="w-5 h-5" />
                      Consultas por Sistema Operativo
                    </h3>
                    <div className="space-y-3">
                      {Object.entries(analyticsData.os_breakdown || {})
                        .sort((a, b) => b[1] - a[1])
                        .map(([os, count]) => {
                          const percentage = analyticsData.total_consultations > 0 
                            ? Math.round(count / analyticsData.total_consultations * 100) 
                            : 0;
                          const colors = {
                            'Android': 'bg-green-500',
                            'iOS': 'bg-slate-800',
                            'Windows': 'bg-blue-500',
                            'macOS': 'bg-slate-600',
                            'Linux': 'bg-orange-500',
                            'ChromeOS': 'bg-yellow-500',
                            'unknown': 'bg-slate-300'
                          };
                          return (
                            <div key={os} className="space-y-1">
                              <div className="flex justify-between text-sm">
                                <span className="font-medium">{os}</span>
                                <span className="text-slate-600">{count} ({percentage}%)</span>
                              </div>
                              <div className="w-full bg-slate-100 rounded-full h-2.5">
                                <div 
                                  className={`${colors[os] || 'bg-slate-400'} h-2.5 rounded-full transition-all`}
                                  style={{ width: `${percentage}%` }}
                                />
                              </div>
                            </div>
                          );
                        })}
                    </div>
                  </Card>

                  {/* Top Tenants Card */}
                  <Card className="p-6 border-slate-200 bg-white lg:col-span-2">
                    <h3 className="text-lg font-semibold text-slate-900 mb-4 flex items-center gap-2">
                      <Building2 className="w-5 h-5" />
                      Top Empresas por Consultas
                    </h3>
                    {analyticsData.top_tenants?.length > 0 ? (
                      <div className="overflow-x-auto">
                        <table className="w-full">
                          <thead className="bg-slate-50">
                            <tr>
                              <th className="px-4 py-2 text-left text-xs font-medium text-slate-600 uppercase">Empresa</th>
                              <th className="px-4 py-2 text-left text-xs font-medium text-slate-600 uppercase">Total</th>
                              <th className="px-4 py-2 text-left text-xs font-medium text-slate-600 uppercase">Móvil</th>
                              <th className="px-4 py-2 text-left text-xs font-medium text-slate-600 uppercase">Desktop</th>
                              <th className="px-4 py-2 text-left text-xs font-medium text-slate-600 uppercase">% Móvil</th>
                            </tr>
                          </thead>
                          <tbody className="divide-y divide-slate-200">
                            {analyticsData.top_tenants.map((tenant) => (
                              <tr key={tenant.slug} className="hover:bg-slate-50">
                                <td className="px-4 py-3 font-medium text-slate-900">{tenant.slug}</td>
                                <td className="px-4 py-3 text-slate-700">{tenant.total}</td>
                                <td className="px-4 py-3 text-violet-600">{tenant.mobile || 0}</td>
                                <td className="px-4 py-3 text-blue-600">{tenant.desktop || 0}</td>
                                <td className="px-4 py-3">
                                  <Badge className={
                                    (tenant.mobile / tenant.total * 100) > 60 
                                      ? 'bg-violet-100 text-violet-700' 
                                      : 'bg-blue-100 text-blue-700'
                                  }>
                                    {tenant.total > 0 ? Math.round(tenant.mobile / tenant.total * 100) : 0}%
                                  </Badge>
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    ) : (
                      <div className="text-center py-8 text-slate-500">
                        <BarChart3 className="w-12 h-12 mx-auto text-slate-300 mb-2" />
                        <p>No hay datos de consultas aún</p>
                      </div>
                    )}
                  </Card>
                </div>
              ) : (
                <div className="text-center py-12">
                  <BarChart3 className="w-16 h-16 text-slate-300 mx-auto mb-4" />
                  <h3 className="text-lg font-semibold text-slate-900 mb-2">
                    No hay datos de analytics
                  </h3>
                  <p className="text-slate-600">
                    Los datos se generarán automáticamente cuando los clientes consulten sus deudas.
                  </p>
                </div>
              )}
            </div>
          </TabsContent>
        </Tabs>
      </div>

      {/* Edit Tenant Modal */}
      <Dialog open={showEdit} onOpenChange={setShowEdit}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto" data-testid="edit-tenant-dialog">
          <DialogHeader>
            <DialogTitle>Editar Empresa: {editingTenant?.name}</DialogTitle>
          </DialogHeader>
          
          {editingTenant && (
            <Tabs defaultValue="general" className="w-full">
              <TabsList className="grid w-full grid-cols-6">
                <TabsTrigger value="general">Datos</TabsTrigger>
                <TabsTrigger value="duenos">Dueños</TabsTrigger>
                <TabsTrigger value="comision">Comisión</TabsTrigger>
                <TabsTrigger value="ultramsg">UltraMsg</TabsTrigger>
                <TabsTrigger value="respondio">Respond.io</TabsTrigger>
                <TabsTrigger value="status">Estado</TabsTrigger>
              </TabsList>
              
              <TabsContent value="general" className="space-y-4 mt-4">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label>Nombre Comercial</Label>
                    <Input
                      value={editingTenant.name}
                      onChange={(e) => setEditingTenant({...editingTenant, name: e.target.value})}
                    />
                  </div>
                  <div>
                    <Label>URL Personalizada (slug)</Label>
                    <div className="flex items-center gap-1">
                      <span className="text-sm text-slate-500 whitespace-nowrap">/t/</span>
                      <Input
                        value={editingTenant.slug || ''}
                        onChange={(e) => setEditingTenant({...editingTenant, slug: e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, '')})}
                        data-testid="edit-tenant-slug"
                      />
                    </div>
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label>Razón Social</Label>
                    <Input
                      value={editingTenant.legal_name || ''}
                      onChange={(e) => setEditingTenant({...editingTenant, legal_name: e.target.value})}
                    />
                  </div>
                </div>
                
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label>CUIT</Label>
                    <Input
                      value={editingTenant.tax_id || ''}
                      onChange={(e) => setEditingTenant({...editingTenant, tax_id: e.target.value})}
                    />
                  </div>
                  <div>
                    <Label>Teléfono</Label>
                    <Input
                      value={editingTenant.phone || ''}
                      onChange={(e) => setEditingTenant({...editingTenant, phone: e.target.value})}
                    />
                  </div>
                </div>
                
                <div>
                  <Label>Dirección Fiscal</Label>
                  <Input
                    value={editingTenant.fiscal_address || ''}
                    onChange={(e) => setEditingTenant({...editingTenant, fiscal_address: e.target.value})}
                  />
                </div>
                
                <div className="grid grid-cols-3 gap-4">
                  <div>
                    <Label>Ciudad</Label>
                    <Input
                      value={editingTenant.city || ''}
                      onChange={(e) => setEditingTenant({...editingTenant, city: e.target.value})}
                    />
                  </div>
                  <div>
                    <Label>Provincia</Label>
                    <Input
                      value={editingTenant.province || ''}
                      onChange={(e) => setEditingTenant({...editingTenant, province: e.target.value})}
                    />
                  </div>
                  <div>
                    <Label>Código Postal</Label>
                    <Input
                      value={editingTenant.postal_code || ''}
                      onChange={(e) => setEditingTenant({...editingTenant, postal_code: e.target.value})}
                    />
                  </div>
                </div>
                
                <Button
                  onClick={handleUpdateTenant}
                  className="w-full bg-teal-600 hover:bg-teal-700"
                >
                  Guardar Cambios
                </Button>
              </TabsContent>
              
              <TabsContent value="duenos" className="space-y-4 mt-4">
                {/* Dueño Principal */}
                <div className="bg-teal-50 p-4 rounded-lg border border-teal-200">
                  <h4 className="font-semibold text-slate-900 mb-2">Dueño Principal</h4>
                  <div>
                    <Label>Email (Gmail)</Label>
                    <Input
                      type="email"
                      value={editingTenant.admin_email || ''}
                      onChange={(e) => setEditingTenant({...editingTenant, admin_email: e.target.value})}
                      placeholder="dueño@gmail.com"
                    />
                  </div>
                </div>

                {/* Dueños adicionales */}
                <div className="bg-slate-50 p-4 rounded-lg">
                  <h4 className="font-semibold text-slate-900 mb-2">Dueños Adicionales</h4>
                  <p className="text-xs text-slate-600 mb-3">
                    Estas personas también pueden administrar la empresa
                  </p>
                  
                  <div className="flex gap-2 mb-3">
                    <Input
                      type="email"
                      value={newOwnerEmail}
                      onChange={(e) => setNewOwnerEmail(e.target.value)}
                      placeholder="otro-dueño@gmail.com"
                      onKeyDown={(e) => {
                        if (e.key === 'Enter') {
                          e.preventDefault();
                          if (newOwnerEmail && newOwnerEmail.includes('@')) {
                            const currentOwners = editingTenant.additional_owners || [];
                            if (!currentOwners.includes(newOwnerEmail)) {
                              setEditingTenant({
                                ...editingTenant,
                                additional_owners: [...currentOwners, newOwnerEmail]
                              });
                            }
                            setNewOwnerEmail('');
                          }
                        }
                      }}
                    />
                    <Button
                      type="button"
                      variant="outline"
                      onClick={() => {
                        if (newOwnerEmail && newOwnerEmail.includes('@')) {
                          const currentOwners = editingTenant.additional_owners || [];
                          if (!currentOwners.includes(newOwnerEmail)) {
                            setEditingTenant({
                              ...editingTenant,
                              additional_owners: [...currentOwners, newOwnerEmail]
                            });
                          }
                          setNewOwnerEmail('');
                        }
                      }}
                    >
                      <Plus className="w-4 h-4" />
                    </Button>
                  </div>

                  {editingTenant.additional_owners?.length > 0 && (
                    <div className="space-y-2">
                      {editingTenant.additional_owners.map((email) => (
                        <div key={email} className="flex items-center justify-between bg-white p-2 rounded border">
                          <div className="flex items-center gap-2">
                            <Users className="w-4 h-4 text-slate-400" />
                            <span className="text-sm">{email}</span>
                          </div>
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => {
                              setEditingTenant({
                                ...editingTenant,
                                additional_owners: editingTenant.additional_owners.filter(e => e !== email)
                              });
                            }}
                            className="text-red-600 hover:bg-red-50"
                          >
                            <X className="w-4 h-4" />
                          </Button>
                        </div>
                      ))}
                    </div>
                  )}
                </div>

                <Button
                  onClick={handleUpdateTenant}
                  className="w-full bg-teal-600 hover:bg-teal-700"
                >
                  Guardar Dueños
                </Button>
              </TabsContent>

              <TabsContent value="comision" className="space-y-4 mt-4">
                <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-4 mb-4">
                  <h4 className="font-semibold text-emerald-900 mb-2">Configuración de Comisión</h4>
                  <p className="text-sm text-emerald-700">
                    Define cómo la plataforma cobra comisiones a esta empresa por los pagos recuperados.
                  </p>
                </div>

                <div>
                  <Label>Tipo de Comisión</Label>
                  <Select
                    value={editingTenant.commission_type || 'fixed'}
                    onValueChange={(value) => setEditingTenant({...editingTenant, commission_type: value})}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="fixed">Monto Fijo por Pago (Escalonado)</SelectItem>
                      <SelectItem value="percentage">Porcentaje del Recupero</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                {editingTenant.commission_type === 'percentage' ? (
                  <div className="bg-slate-50 p-4 rounded-lg">
                    <Label>Porcentaje de Comisión (%)</Label>
                    <Input
                      type="number"
                      min="0"
                      max="100"
                      step="0.1"
                      value={editingTenant.commission_percentage || ''}
                      onChange={(e) => setEditingTenant({...editingTenant, commission_percentage: e.target.value})}
                      placeholder="10"
                    />
                    <p className="text-xs text-slate-500 mt-1">
                      Ej: 10% significa que por cada $100 recuperados, se cobra $10
                    </p>
                  </div>
                ) : (
                  <div className="bg-slate-50 p-4 rounded-lg space-y-3">
                    <div className="flex justify-between items-center">
                      <Label>Tramos de Comisión</Label>
                      <Button size="sm" variant="outline" onClick={addEditTier}>
                        <Plus className="w-4 h-4 mr-1" /> Agregar Tramo
                      </Button>
                    </div>
                    
                    {(editingTenant.commission_tiers || [{ from_payments: 0, to_payments: 10000, amount_per_payment: 50 }]).map((tier, idx) => (
                      <div key={`edit-tier-${tier.from_payments}-${tier.percentage}`} className="grid grid-cols-4 gap-2 items-end bg-white p-3 rounded border">
                        <div>
                          <Label className="text-xs">Desde pagos</Label>
                          <Input
                            type="number"
                            value={tier.from_payments}
                            onChange={(e) => updateEditTier(idx, 'from_payments', e.target.value)}
                          />
                        </div>
                        <div>
                          <Label className="text-xs">Hasta pagos</Label>
                          <Input
                            type="number"
                            value={tier.to_payments || ''}
                            onChange={(e) => updateEditTier(idx, 'to_payments', e.target.value || null)}
                            placeholder="∞"
                          />
                        </div>
                        <div>
                          <Label className="text-xs">$ por pago</Label>
                          <Input
                            type="number"
                            step="0.01"
                            value={tier.amount_per_payment}
                            onChange={(e) => updateEditTier(idx, 'amount_per_payment', e.target.value)}
                          />
                        </div>
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => removeEditTier(idx)}
                          disabled={(editingTenant.commission_tiers || []).length <= 1}
                          className="text-red-600 hover:bg-red-50"
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    ))}
                    <p className="text-xs text-slate-500">
                      Ej: 0-10,000 pagos = $50/pago, 10,001+ = $45/pago
                    </p>
                  </div>
                )}

                <Button
                  onClick={handleUpdateCommission}
                  className="w-full bg-emerald-600 hover:bg-emerald-700"
                >
                  Guardar Configuración de Comisión
                </Button>
              </TabsContent>
              
              <TabsContent value="ultramsg" className="space-y-4 mt-4">
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-4">
                  <h4 className="font-semibold text-blue-900 mb-2">Configuración UltraMsg</h4>
                  <p className="text-sm text-blue-700">
                    UltraMsg se usa para enviar códigos de verificación por WhatsApp a los clientes.
                  </p>
                </div>
                
                <div>
                  <Label>Instance ID</Label>
                  <Input
                    value={editingTenant.ultramsg_instance_id || ''}
                    onChange={(e) => setEditingTenant({...editingTenant, ultramsg_instance_id: e.target.value})}
                    placeholder="instance12345"
                  />
                </div>
                
                <div>
                  <Label>Token</Label>
                  <Input
                    value={editingTenant.ultramsg_token || ''}
                    onChange={(e) => setEditingTenant({...editingTenant, ultramsg_token: e.target.value})}
                    placeholder="abcdef123456..."
                    type="password"
                  />
                </div>
                
                <Button
                  onClick={handleUpdateUltraMsg}
                  className="w-full bg-blue-600 hover:bg-blue-700"
                >
                  Guardar Configuración UltraMsg
                </Button>
              </TabsContent>
              
              <TabsContent value="respondio" className="space-y-4 mt-4">
                <div className="bg-green-50 border border-green-200 rounded-lg p-4 mb-4">
                  <h4 className="font-semibold text-green-900 mb-2 flex items-center gap-2">
                    <MessageSquare className="w-5 h-5" />
                    Configuración Respond.io
                  </h4>
                  <p className="text-sm text-green-700">
                    Respond.io se usa para enviar mensajes de WhatsApp con templates aprobados a los clientes.
                  </p>
                </div>
                
                {/* Status indicator */}
                {respondioStatus[editingTenant.id] && (
                  <div className={`p-3 rounded-lg ${
                    respondioStatus[editingTenant.id].connected 
                      ? 'bg-emerald-50 border border-emerald-200' 
                      : 'bg-amber-50 border border-amber-200'
                  }`}>
                    <div className="flex items-center gap-2">
                      {respondioStatus[editingTenant.id].connected ? (
                        <Wifi className="w-4 h-4 text-emerald-600" />
                      ) : (
                        <WifiOff className="w-4 h-4 text-amber-600" />
                      )}
                      <span className={`text-sm font-medium ${
                        respondioStatus[editingTenant.id].connected ? 'text-emerald-700' : 'text-amber-700'
                      }`}>
                        {respondioStatus[editingTenant.id].message}
                        {respondioStatus[editingTenant.id].templates_count > 0 && (
                          <span className="ml-2">({respondioStatus[editingTenant.id].templates_count} templates)</span>
                        )}
                      </span>
                    </div>
                  </div>
                )}
                
                <div>
                  <Label>API Token *</Label>
                  <Input
                    type="password"
                    value={editingTenant.respondio_token || ''}
                    onChange={(e) => setEditingTenant({...editingTenant, respondio_token: e.target.value})}
                    placeholder="eyJhbGciOiJIUzI1NiIs..."
                    className="font-mono text-sm"
                  />
                  <p className="text-xs text-slate-500 mt-1">
                    Obtener en: Workspace Settings → Integrations → Developer API
                  </p>
                </div>
                
                <div>
                  <Label>Channel ID *</Label>
                  <Input
                    value={editingTenant.respondio_channel_id || ''}
                    onChange={(e) => setEditingTenant({...editingTenant, respondio_channel_id: e.target.value})}
                    placeholder="448909"
                  />
                  <p className="text-xs text-slate-500 mt-1">
                    ID del canal de WhatsApp en Respond.io
                  </p>
                </div>
                
                <div>
                  <Label>Space ID (opcional)</Label>
                  <Input
                    value={editingTenant.respondio_space_id || ''}
                    onChange={(e) => setEditingTenant({...editingTenant, respondio_space_id: e.target.value})}
                    placeholder="134798"
                  />
                </div>
                
                <div className="flex gap-2">
                  <Button
                    onClick={() => loadRespondioStatus(editingTenant.id)}
                    variant="outline"
                    className="flex-1"
                  >
                    <RefreshCw className="w-4 h-4 mr-2" />
                    Verificar Conexión
                  </Button>
                  <Button
                    onClick={handleUpdateRespondio}
                    disabled={savingRespondio}
                    className="flex-1 bg-green-600 hover:bg-green-700"
                  >
                    {savingRespondio ? 'Guardando...' : 'Guardar Configuración'}
                  </Button>
                </div>
              </TabsContent>
              
              <TabsContent value="status" className="space-y-4 mt-4">
                <div className="bg-slate-50 border border-slate-200 rounded-lg p-6">
                  <div className="flex items-center justify-between">
                    <div>
                      <h4 className="font-semibold text-slate-900 mb-1">Estado de la Empresa</h4>
                      <p className="text-sm text-slate-600">
                        {editingTenant.is_active ? 'La empresa está activa y operativa' : 'La empresa está desactivada'}
                      </p>
                    </div>
                    <Switch
                      checked={editingTenant.is_active}
                      onCheckedChange={(checked) => setEditingTenant({...editingTenant, is_active: checked})}
                    />
                  </div>
                </div>
                
                <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
                  <p className="text-sm text-amber-800">
                    <strong>Nota:</strong> Al desactivar una empresa, sus clientes no podrán acceder al portal de consulta.
                  </p>
                </div>
                
                <Button
                  onClick={handleUpdateTenant}
                  className="w-full bg-teal-600 hover:bg-teal-700"
                >
                  Guardar Estado
                </Button>
              </TabsContent>
            </Tabs>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default SuperAdminDashboard;